import { DateTime } from "luxon";
import { cloneDeep } from "lodash-es";

import LocalForagePlugin, { syncState, tickers } from "@/plugins/LocalForagePlugin";
import { tickersSyncKey } from "@/types/products/product-definitions";

import { goFetch } from "@/utils/network-utils";
import type { PersistedQueryPayload } from "@/types/api/fetch";

import type { SearchableTicker, Ticker } from "@/types/entities";
import type { Tickers } from "@/types/api/content-models";

import { consoleLoggerFactory, getDefaultLoggerOptions, LogLevels } from "@/types/console-logger";
const logger = consoleLoggerFactory.create( getDefaultLoggerOptions( LogLevels.ERROR ) );

export async function refreshTickersFromServer(): Promise< SearchableTicker[] > {
    let allTickers: SearchableTicker[] = [];
    // Timing - testing to see how expensive each method is
    const t0: number = performance.now();

    let page: number = 0;
    const pageSize: number = 2_000;

    let tickerPayload: SearchableTicker[] | undefined = [];

    do {
        const response =
            await goFetch< Tickers, PersistedQueryPayload >( {
                endpoint: 'content/graphql',
                payload: {
                    id: '70776b66f641d47e6357f1fd3ed79a56',
                    variables: {
                        skip: page,
                        limit: pageSize
                    }
                }
            } );

        if ( response?.data?.tickerCatalog?.tickers ) {
            tickerPayload = [ ...( response.data.tickerCatalog.tickers
                    .map( ( t: Ticker ): SearchableTicker => ( {
                        id: t.id,
                        name: t.company,
                        ticker: t.name,
                        isEtf: t.is_etf
                    } ) )
            ) ];

            const t1: number = performance.now();
            logger.info( `Fetching & mapping ${ tickerPayload.length } ticker items took ${ t1 - t0 } ms` );

            allTickers = [ ...tickerPayload, ...allTickers ];
        }

        // update the page #
        page++;
    } while ( (tickerPayload?.length ?? 0) >= pageSize );

    return allTickers;
}

export async function loadTickerMap( tickersMap: Map< string, SearchableTicker > ): Promise< boolean > {
    let updated = false;
    try {
        const t0: number = performance.now();

        if ( typeof tickers === 'undefined' || typeof syncState === 'undefined' ) {
            // Wait for the driver to settle
            LocalForagePlugin.initialize();
        }

        await syncState.ready();
        // Check for the next refresh interval
        const syncThresholdInMillis = await syncState.getItem< number >( tickersSyncKey );
        if ( null == syncThresholdInMillis || syncThresholdInMillis < DateTime.utc().toMillis() ) {

            const nextSyncThresholdInMillis = DateTime.utc().plus( { minutes: 30 } ).toMillis();
            await syncState.setItem( tickersSyncKey, nextSyncThresholdInMillis );

            // Retrieve the latest collection of tickers from the content service
            const tickersReceived: SearchableTicker[] = await refreshTickersFromServer();

            if(tickersReceived.length > 0)
            {
                updated = true;

                // Store the items recently retrieved
                await tickers.ready();
                for ( const ticker of tickersReceived ) {
                    await tickers.setItem( ticker.id, cloneDeep( ticker ) );
                }

                const t1: number = performance.now();
                logger.info( `[ sync-worker ] Reset cache for ${ tickersReceived.length } tickers in ${ t1 - t0 } ms` );
            }
            else{

                const nextSyncThresholdInMillis = DateTime.utc().plus( { seconds: 15 } ).toMillis();
                await syncState.setItem( tickersSyncKey, nextSyncThresholdInMillis );
            }
        }

        // Make sure map is empty
        tickersMap.clear();

        // Load whatever is in the local cache
        await tickers.ready();
        // Iterate over all elements within IDB
        await tickers.iterate( ( ticker: SearchableTicker ) => {
            tickersMap.set( ticker.id, ticker );
            return;
        } );

    } catch ( err: any ) {
        logger.error( JSON.stringify( err.message ) );
    }

    return updated;
}
